import React, { useState, useEffect } from 'react';
import { UserPlus, Copy, Trash2, RefreshCw, Eye, EyeOff, Users, Mail, Key, Clock, Timer, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config';

const DemoAccountsManager = ({ token, i18n }) => {
  const [demoAccounts, setDemoAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [customName, setCustomName] = useState('');
  const [expirationHours, setExpirationHours] = useState('24');
  const [now, setNow] = useState(new Date());

  // Update current time every minute for countdown display
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchDemoAccounts();
  }, []);

  const fetchDemoAccounts = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/demo-accounts`, { headers });
      setDemoAccounts(res.data.accounts || []);
    } catch (error) {
      console.error('Error fetching demo accounts:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في جلب الحسابات التجريبية' : 'Error fetching demo accounts');
    } finally {
      setLoading(false);
    }
  };

  const createDemoAccount = async () => {
    if (!customName.trim()) {
      toast.error(i18n.language === 'ar' ? 'يرجى إدخال اسم للحساب' : 'Please enter an account name');
      return;
    }

    // Validate: only letters, numbers, dots, underscores
    const nameRegex = /^[a-zA-Z0-9._]+$/;
    if (!nameRegex.test(customName)) {
      toast.error(i18n.language === 'ar' ? 'الاسم يجب أن يحتوي فقط على حروف وأرقام ونقاط وشرطات سفلية' : 'Name can only contain letters, numbers, dots and underscores');
      return;
    }

    setCreating(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/demo-accounts/create`, { 
        custom_name: customName.trim(),
        expiration_hours: parseInt(expirationHours) 
      }, { headers });
      
      if (res.data.success) {
        toast.success(
          i18n.language === 'ar' 
            ? `تم إنشاء الحساب: ${res.data.created.email}` 
            : `Account created: ${res.data.created.email}`
        );
        setCustomName('');
        fetchDemoAccounts();
      }
    } catch (error) {
      console.error('Error creating demo account:', error);
      const errorMsg = error.response?.data?.detail || (i18n.language === 'ar' ? 'خطأ في إنشاء الحساب' : 'Error creating account');
      toast.error(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const deleteDemoAccount = async (accountId) => {
    if (!window.confirm(i18n.language === 'ar' ? 'هل أنت متأكد من حذف هذا الحساب؟' : 'Are you sure you want to delete this account?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/demo-accounts/${accountId}`, { headers });
      toast.success(i18n.language === 'ar' ? 'تم حذف الحساب' : 'Account deleted');
      fetchDemoAccounts();
    } catch (error) {
      console.error('Error deleting demo account:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الحذف' : 'Error deleting account');
    }
  };

  const deleteAllDemoAccounts = async () => {
    if (!window.confirm(i18n.language === 'ar' ? 'هل أنت متأكد من حذف جميع الحسابات التجريبية؟' : 'Are you sure you want to delete ALL demo accounts?')) {
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/demo-accounts/all`, { headers });
      toast.success(i18n.language === 'ar' ? 'تم حذف جميع الحسابات' : 'All demo accounts deleted');
      fetchDemoAccounts();
    } catch (error) {
      console.error('Error deleting all demo accounts:', error);
      toast.error(i18n.language === 'ar' ? 'خطأ في الحذف' : 'Error deleting accounts');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(i18n.language === 'ar' ? 'تم النسخ!' : 'Copied!');
  };

  const copyAllCredentials = () => {
    const text = demoAccounts.map(acc => 
      `${i18n.language === 'ar' ? 'البريد' : 'Email'}: ${acc.email}\n${i18n.language === 'ar' ? 'كلمة السر' : 'Password'}: ${acc.password}`
    ).join('\n\n');
    navigator.clipboard.writeText(text);
    toast.success(i18n.language === 'ar' ? 'تم نسخ جميع البيانات!' : 'All credentials copied!');
  };

  const togglePassword = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getRemainingTime = (expiresAt) => {
    if (!expiresAt) return null;
    
    const expiry = new Date(expiresAt);
    const diff = expiry - now;
    
    if (diff <= 0) {
      return { expired: true, text: i18n.language === 'ar' ? 'منتهي' : 'Expired' };
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return { 
        expired: false, 
        text: i18n.language === 'ar' ? `${days} يوم ${hours % 24} ساعة` : `${days}d ${hours % 24}h`,
        urgent: false
      };
    } else if (hours > 0) {
      return { 
        expired: false, 
        text: i18n.language === 'ar' ? `${hours} ساعة ${minutes} دقيقة` : `${hours}h ${minutes}m`,
        urgent: hours < 2
      };
    } else {
      return { 
        expired: false, 
        text: i18n.language === 'ar' ? `${minutes} دقيقة` : `${minutes}m`,
        urgent: true
      };
    }
  };

  const expirationOptions = [
    { value: '1', label: i18n.language === 'ar' ? 'ساعة واحدة' : '1 hour' },
    { value: '2', label: i18n.language === 'ar' ? 'ساعتين' : '2 hours' },
    { value: '6', label: i18n.language === 'ar' ? '6 ساعات' : '6 hours' },
    { value: '12', label: i18n.language === 'ar' ? '12 ساعة' : '12 hours' },
    { value: '24', label: i18n.language === 'ar' ? 'يوم واحد' : '1 day' },
    { value: '48', label: i18n.language === 'ar' ? 'يومين' : '2 days' },
    { value: '72', label: i18n.language === 'ar' ? '3 أيام' : '3 days' },
    { value: '168', label: i18n.language === 'ar' ? 'أسبوع' : '1 week' },
    { value: '0', label: i18n.language === 'ar' ? 'بدون انتهاء' : 'No expiration' },
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-xl p-8 border border-slate-200">
        <div className="flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-3 mb-2">
          <UserPlus className="w-8 h-8" />
          <h3 className="text-2xl font-bold">
            {i18n.language === 'ar' ? 'الحسابات التجريبية' : 'Demo Accounts'}
          </h3>
        </div>
        <p className="text-white/80">
          {i18n.language === 'ar' 
            ? 'إنشاء حسابات وهمية للتجربة بإيميلات @drivemaster.se مع موقت للحذف التلقائي' 
            : 'Create demo accounts with @drivemaster.se emails with auto-delete timer'}
        </p>
      </div>

      {/* Create Section */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-emerald-600" />
          {i18n.language === 'ar' ? 'إنشاء حساب جديد' : 'Create New Account'}
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Custom name input */}
          <div>
            <Label className="text-sm font-medium mb-2 block flex items-center gap-1">
              <User className="w-4 h-4" />
              {i18n.language === 'ar' ? 'اسم الحساب' : 'Account Name'}
            </Label>
            <div className="flex">
              <Input
                type="text"
                placeholder={i18n.language === 'ar' ? 'مثال: ahmed' : 'e.g., ahmed'}
                value={customName}
                onChange={(e) => setCustomName(e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, ''))}
                className="rounded-r-none"
                data-testid="demo-account-name-input"
              />
              <span className="inline-flex items-center px-3 bg-slate-100 border border-l-0 border-slate-300 rounded-r-md text-slate-600 text-sm">
                @drivemaster.se
              </span>
            </div>
          </div>
          
          {/* Expiration time */}
          <div>
            <Label className="text-sm font-medium mb-2 block flex items-center gap-1">
              <Timer className="w-4 h-4" />
              {i18n.language === 'ar' ? 'مدة الصلاحية' : 'Expiration Time'}
            </Label>
            <Select value={expirationHours} onValueChange={setExpirationHours}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {expirationOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Create button */}
          <div className="flex items-end">
            <Button 
              onClick={createDemoAccount} 
              disabled={creating || !customName.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 w-full"
              data-testid="create-demo-account-btn"
            >
              {creating ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              {i18n.language === 'ar' ? 'إنشاء' : 'Create'}
            </Button>
          </div>
        </div>

        <p className="text-sm text-slate-500 mt-3">
          {customName ? (
            <span className="text-emerald-600 font-medium">
              {i18n.language === 'ar' 
                ? `سيتم إنشاء: ${customName}@drivemaster.se` 
                : `Will create: ${customName}@drivemaster.se`}
            </span>
          ) : (
            i18n.language === 'ar' 
              ? 'أدخل اسم الحساب الذي تريده (حروف إنجليزية وأرقام فقط)' 
              : 'Enter the account name you want (English letters and numbers only)'
          )}
        </p>
      </div>

      {/* Existing Accounts */}
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            {i18n.language === 'ar' ? 'الحسابات الموجودة' : 'Existing Accounts'}
            <span className="bg-blue-100 text-blue-700 text-sm px-2 py-0.5 rounded-full ml-2">
              {demoAccounts.length}
            </span>
          </h4>
          
          {demoAccounts.length > 0 && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={copyAllCredentials}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <Copy className="w-4 h-4 mr-1" />
                {i18n.language === 'ar' ? 'نسخ الكل' : 'Copy All'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={deleteAllDemoAccounts}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {i18n.language === 'ar' ? 'حذف الكل' : 'Delete All'}
              </Button>
            </div>
          )}
        </div>

        {demoAccounts.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium">
              {i18n.language === 'ar' ? 'لا توجد حسابات تجريبية' : 'No demo accounts yet'}
            </p>
            <p className="text-sm">
              {i18n.language === 'ar' ? 'قم بإنشاء حساب جديد أعلاه' : 'Create a new account above'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {demoAccounts.map((account) => {
              const remaining = getRemainingTime(account.expires_at);
              return (
                <div 
                  key={account.id} 
                  className={`flex flex-wrap items-center gap-4 p-4 rounded-lg border transition-all ${
                    remaining?.expired 
                      ? 'bg-red-50 border-red-200' 
                      : remaining?.urgent 
                        ? 'bg-amber-50 border-amber-200' 
                        : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`demo-account-${account.id}`}
                >
                  {/* Email */}
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                      <Mail className="w-4 h-4" />
                      {i18n.language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-white px-3 py-1.5 rounded border text-slate-900 font-mono text-sm flex-1 truncate">
                        {account.email}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(account.email)}
                        className="hover:bg-blue-50 shrink-0"
                      >
                        <Copy className="w-4 h-4 text-blue-600" />
                      </Button>
                    </div>
                  </div>

                  {/* Password */}
                  <div className="flex-1 min-w-[180px]">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                      <Key className="w-4 h-4" />
                      {i18n.language === 'ar' ? 'كلمة السر' : 'Password'}
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-white px-3 py-1.5 rounded border text-slate-900 font-mono text-sm flex-1">
                        {showPasswords[account.id] ? account.password : '••••••••'}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePassword(account.id)}
                        className="hover:bg-slate-100 shrink-0"
                      >
                        {showPasswords[account.id] ? (
                          <EyeOff className="w-4 h-4 text-slate-600" />
                        ) : (
                          <Eye className="w-4 h-4 text-slate-600" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(account.password)}
                        className="hover:bg-blue-50 shrink-0"
                      >
                        <Copy className="w-4 h-4 text-blue-600" />
                      </Button>
                    </div>
                  </div>

                  {/* Timer */}
                  <div className="min-w-[120px]">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
                      <Clock className="w-4 h-4" />
                      {i18n.language === 'ar' ? 'الوقت المتبقي' : 'Time Left'}
                    </div>
                    {remaining ? (
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded font-medium text-sm ${
                        remaining.expired 
                          ? 'bg-red-100 text-red-700' 
                          : remaining.urgent 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        <Timer className="w-4 h-4" />
                        {remaining.text}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-1.5 rounded font-medium text-sm bg-slate-100 text-slate-600">
                        <Timer className="w-4 h-4" />
                        {i18n.language === 'ar' ? 'بدون انتهاء' : 'No expiry'}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteDemoAccount(account.id)}
                      className="hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
        <h4 className="text-lg font-semibold text-amber-800 mb-3">
          {i18n.language === 'ar' ? '💡 كيفية الاستخدام' : '💡 How to Use'}
        </h4>
        <ul className="space-y-2 text-amber-700">
          <li className="flex items-start gap-2">
            <span className="font-bold">1.</span>
            {i18n.language === 'ar' 
              ? 'اختر اسم الحساب (مثل: ahmed, test, friend1) ومدة الصلاحية' 
              : 'Choose account name (e.g., ahmed, test, friend1) and expiration time'}
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">2.</span>
            {i18n.language === 'ar' 
              ? 'انسخ بيانات الدخول وشاركها مع من يريد تجربة الموقع' 
              : 'Copy the credentials and share with people who want to try the site'}
          </li>
          <li className="flex items-start gap-2">
            <span className="font-bold">3.</span>
            {i18n.language === 'ar' 
              ? 'الحسابات ستُحذف تلقائياً بعد انتهاء الوقت المحدد' 
              : 'Accounts will be automatically deleted after the set time'}
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DemoAccountsManager;
